import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, PopupContentContainer, RecordOffIcon, RecordOnIcon, WarningIcon, } from '@streamr/tv-ui';
import { useMemo, useState } from 'react';
import { css, styled } from 'styled-components';
import { useIntervalEffect } from '../../../hooks/useIntervalEffect';
import { BroadcastService } from '../../../services/broadcast/BroadcastService';
import { RecordingService } from '../../../services/recording/RecordingService';
import { assert } from '../../../utils/assert';
import { BottomRight } from '../../../utils/popups';
import { PopupCssPreset } from '../../popups/Popup.styles';
import { showStartRecordingPopup } from '../../popups/recording/StartRecordingPopup';
import { showStopRecordingPopup, } from '../../popups/recording/StopRecordingPopup';
import { showToast } from '../../popups/ToastPopup';
const ONE_SECOND = 1000;
const mobilePositioningForStopRecordingPopup = {
    fullscreenOnly: true,
};
const desktopPositioningForStopRecordingPopup = {
    fullscreenOnly: false,
    translateFn: BottomRight,
    popupCssPresetForDesktop: PopupCssPreset.AnchoredBottomRight,
};
/*
    This button is just a part of the BroadcastPopup component. It was made as a separate component to not increase the complexity of the BroadcastPopup component.
*/
export const PopupRecordButton = ({ anchorRef, variant, broadcast, closePopup, }) => {
    const isBroadcasting = BroadcastService.getOutput().useBroadcasting();
    const activeRecording = RecordingService.getOutput().useActiveRecording();
    const initializationDurationMs = RecordingService.getOutput().useInitializationDurationMs();
    const onStartRecording = async () => {
        closePopup();
        const access = await showStartRecordingPopup({
            anchorRef,
            variant,
        });
        if (access) {
            RecordingService.getNotificationInterface().notifyStartRecording(broadcast, access);
        }
    };
    const onCancelInitialization = () => {
        RecordingService.getNotificationInterface().notifyStopRecording();
    };
    const onStopRecording = async () => {
        assert(activeRecording, 'Recording is not active');
        closePopup();
        const shouldStop = await showStopRecordingPopup({
            anchorRef,
            positioning: variant === 'mobile'
                ? mobilePositioningForStopRecordingPopup
                : desktopPositioningForStopRecordingPopup,
            broadcast,
            isPublic: activeRecording.visibility === 'public',
        });
        if (shouldStop) {
            RecordingService.getNotificationInterface().notifyStopRecording();
        }
    };
    const onRetry = () => {
        RecordingService.getNotificationInterface().notifyRetryInitialization();
    };
    const [now, setNow] = useState(() => Date.now());
    const intervalValue = activeRecording && activeRecording.status === 'initializing'
        ? ONE_SECOND
        : undefined;
    useIntervalEffect(() => {
        setNow(Date.now());
    }, intervalValue);
    const handleRecordButtonClick = () => {
        if (!isBroadcasting) {
            showToast('You need to Go Live first to start recording');
            return;
        }
        if (activeRecording?.status === 'recording') {
            onStopRecording();
        }
        else if (activeRecording?.status === 'initializing') {
            onCancelInitialization();
        }
        else if (activeRecording?.status === 'error') {
            onRetry();
        }
        else {
            onStartRecording();
        }
    };
    const recordButtonText = useMemo(() => {
        if (activeRecording?.status === 'initializing') {
            const remainingMs = initializationDurationMs -
                (now - (activeRecording.initializedAt ?? 0));
            const remainingSeconds = Math.ceil(remainingMs / 1000);
            return remainingSeconds > 0
                ? String(remainingSeconds)
                : 'Almost there...';
        }
        if (activeRecording?.status === 'recording') {
            return 'Stop recording';
        }
        if (activeRecording?.status === 'error') {
            return 'Error';
        }
        if (activeRecording?.status === 'stopping') {
            return 'Stopping...';
        }
        return 'Record';
    }, [activeRecording, now, initializationDurationMs]);
    return (_jsx(StyledButton, { type: "button", onClick: handleRecordButtonClick, children: _jsxs(ButtonContent, { "$status": activeRecording?.status, children: [activeRecording?.status === 'error' && (_jsx(WarningIcon, { fill: "var(--tv-ui-color-6)" })), activeRecording?.status === 'recording' && _jsx(RecordOnIcon, {}), (activeRecording?.status === 'initializing' ||
                    activeRecording?.status === 'stopping' ||
                    activeRecording === undefined) && _jsx(RecordOffIcon, {}), _jsx(ButtonText, { "$status": activeRecording?.status, children: recordButtonText })] }) }));
};
const StyledButton = styled(Button) `
    --Button_Transform: translate(0);
    --Button_HoverTransform: translate(0);
    --Button_ActiveTransform: translate(0);
    --Button_HoverBackground: inherit;
`;
const ButtonContent = styled(PopupContentContainer) `
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    ${({ $status }) => $status === 'initializing' &&
    css `
        background-color: var(--tv-ui-color-3);
        color: var(--tv-ui-color-6);

    `}
    ${({ $status }) => $status === 'recording' &&
    css `
        background-color: var(--tv-ui-color-6);
        color: var(--tv-ui-color-3);
    `}
`;
const ButtonText = styled.span `
    ${({ $status }) => $status === 'initializing' &&
    css `
        color: var(--tv-ui-color-1);
    `}
`;
